import type { iProcess } from '../components/type';
import type { RecentCollectCollectionSummary } from '../services/api/collectDataService';
import { getProcessModifiedTimestamp } from './formatDate';
import type { HomeRecentMergedEntry } from './homeRecentMerge';

function parseSortTs(value: unknown): number {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const d = new Date(value as string);
    const n = d.getTime();
    return Number.isFinite(n) ? n : 0;
}

function normalizeSearchText(value: string): string {
    return value.trim().toLowerCase();
}

function fieldMatchesQuery(field: unknown, query: string): boolean {
    if (field == null) return false;
    const text = String(field).trim();
    if (!text) return false;
    return normalizeSearchText(text).includes(normalizeSearchText(query));
}

/** Client-side search for data collections (draft, active, closed) when API only returns process mappings. */
export function filterDataCollectionsForSearch(
    collections: RecentCollectCollectionSummary[],
    searchQuery: string,
    filter?: string,
): RecentCollectCollectionSummary[] {
    const q = searchQuery.trim();
    if (!q) return [];

    if (filter === 'ROLE') {
        return [];
    }

    return collections.filter((col) => {
        if (filter === 'TITLE') {
            return (
                fieldMatchesQuery(col.name, q) ||
                fieldMatchesQuery(col.business_objective_preview, q)
            );
        }

        return (
            fieldMatchesQuery(col.name, q) ||
            fieldMatchesQuery(col.business_objective_preview, q) ||
            fieldMatchesQuery(col.owner_display_name, q)
        );
    });
}

export function mergeProcessSearchWithDataCollections(
    processes: iProcess[],
    collections: RecentCollectCollectionSummary[],
    searchQuery: string,
    filter?: string,
): HomeRecentMergedEntry[] {
    const matchedCollections = filterDataCollectionsForSearch(collections, searchQuery, filter);
    const out: HomeRecentMergedEntry[] = [];

    for (const p of processes) {
        const raw = getProcessModifiedTimestamp(p as unknown as Record<string, unknown>);
        out.push({ kind: 'process', item: p, sortKey: parseSortTs(raw) });
    }
    for (const c of matchedCollections) {
        out.push({ kind: 'dataCollect', item: c, sortKey: parseSortTs(c.updated_at ?? c.created_at) });
    }

    out.sort((a, b) => b.sortKey - a.sortKey);
    return out;
}
